"use client";

import { useTransition } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";

import { setLocale } from "@/app/actions/locale";
import { cn } from "@/lib/utils";

const LOCALES = [
  { code: "en", label: "EN" },
  { code: "ru", label: "RU" },
] as const;

/**
 * Compact EN/RU language switcher. Persists the choice via the NEXT_LOCALE
 * cookie (server action) then refreshes so server components re-render with the
 * new catalog. Two visual variants:
 *  - "segmented" (default): a pill toggle matching the nav/tabs — active locale
 *    is a filled card pill. Used in the top nav.
 *  - "inline": plain text buttons with a slash, used on the login page.
 */
export function LanguageSwitcher({
  variant = "segmented",
  className,
}: {
  variant?: "segmented" | "inline";
  className?: string;
}) {
  const locale = useLocale();
  const t = useTranslations("common");
  const router = useRouter();
  const [isPending, startTransition] = useTransition();

  function select(code: string) {
    if (code === locale || isPending) return;
    startTransition(async () => {
      await setLocale(code);
      router.refresh();
    });
  }

  if (variant === "inline") {
    return (
      <div
        className={cn(
          "flex items-center justify-center gap-1 text-xs",
          className,
        )}
        aria-label={t("language")}
      >
        {LOCALES.map((l, i) => (
          <span key={l.code} className="flex items-center gap-1">
            {i > 0 && <span className="text-muted-foreground/50">/</span>}
            <button
              type="button"
              onClick={() => select(l.code)}
              disabled={isPending}
              aria-pressed={locale === l.code}
              className={cn(
                "rounded px-1.5 py-0.5 font-medium transition-colors",
                locale === l.code
                  ? "text-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {l.label}
            </button>
          </span>
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-0.5 rounded-xl border bg-muted/40 p-0.5",
        className,
      )}
      role="group"
      aria-label={t("language")}
    >
      {LOCALES.map((l) => (
        <button
          key={l.code}
          type="button"
          onClick={() => select(l.code)}
          disabled={isPending}
          aria-pressed={locale === l.code}
          className={cn(
            "rounded-lg px-2.5 py-1.5 text-xs font-semibold transition-colors",
            locale === l.code
              ? "bg-card text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}
